import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import * as adminApi from '@/services/endpoints/admin'
import type { SocialLinks, SocialLinksPayload } from '@/types/admin'

/** One icon of the sidebar footer, already resolved to a usable link. */
export interface SocialEntry {
  key: keyof SocialLinks
  url: string
  /** i18n key of the accessible name — the store never holds translated text. */
  label: string
}

/** Fixed display order; only the URL of each network comes from the API. */
const NETWORKS: { key: keyof SocialLinks; label: string }[] = [
  { key: 'discord', label: 'common.social.discord' },
  { key: 'telegram', label: 'common.social.telegram' },
  { key: 'instagram', label: 'common.social.instagram' },
]

/** Only these two schemes ever reach an `href`; anything else is dropped. */
function isSafeUrl(value: string | null | undefined): value is string {
  if (!value) return false
  try {
    const { protocol } = new URL(value)
    return protocol === 'http:' || protocol === 'https:'
  } catch {
    return false
  }
}

/**
 * Social links shown at the bottom of the sidebar, set from the admin panel.
 *
 * `GET /admin/social` needs no token, exactly like the maintenance state: the
 * sidebar exists for anonymous visitors browsing the menu preview too.
 */
export const useSocialStore = defineStore('social', () => {
  const links = ref<SocialLinks | null>(null)
  const loaded = ref(false)
  // Shared promise: the sidebar is mounted twice below `lg` (docked + drawer),
  // and `loaded` only flips when the first call has already come back.
  let inFlight: Promise<SocialLinks | null> | null = null

  /**
   * The configured networks, in display order. The backend already refuses
   * anything but http/https, but the check is repeated here: this value goes
   * straight into an `href` and the API is not the only thing that can change.
   */
  const entries = computed<SocialEntry[]>(() =>
    NETWORKS.flatMap(({ key, label }) => {
      const url = links.value?.[key]
      return isSafeUrl(url) ? [{ key, url, label }] : []
    }),
  )

  function fetch(force = false): Promise<SocialLinks | null> {
    if (loaded.value && !force) return Promise.resolve(links.value)
    if (inFlight && !force) return inFlight

    inFlight = adminApi
      .getSocial()
      .then((result) => {
        links.value = result
        return result
      })
      .catch(() => {
        // Decorative: an unreachable endpoint just means no icons, never an
        // error toast on top of every page load.
        links.value = null
        return null
      })
      .finally(() => {
        loaded.value = true
        inFlight = null
      })

    return inFlight
  }

  /** Admin action: saves the links and keeps the local copy in sync. */
  async function update(payload: SocialLinksPayload): Promise<SocialLinks> {
    const updated = await adminApi.setSocial(payload)
    links.value = updated
    loaded.value = true
    return updated
  }

  return { links, loaded, entries, fetch, update }
})
